import { Injectable } from '@angular/core';

import { ApiService } from '@frontend/sports/common/api-utils';
import { OddsConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollFreezeServiceTypes } from '@frontend/sports/common/core/feature/scroll-freeze';
import { DialogSettings, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { RegistrationInfoService, UserService } from '@frontend/vanilla/core';
import { PlayerAttributesService } from '@frontend/vanilla/features/player-attributes';
import { PlayerAttributes } from 'packages/vanilla/lib/features/player-attributes/src/player-attributes.models';
import { map, of, take } from 'rxjs';

import { PlayerPreference } from './model/odd-changes-confirmation.model';
import { OddChangesConfirmationComponent } from './odd-changes-confirmation.component';

@Injectable({ providedIn: 'root' })
export class OddChangeConfirmationService {
    private _modalRef: ModalRef;

    constructor(
        private apiService: ApiService,
        private modalDialogService: ModalDialogService,
        private userService: UserService,
        public oddsConfig: OddsConfig,
        private playerAttributesService: PlayerAttributesService,
        private registrationInfoService: RegistrationInfoService,
    ) {}

    openOddChangesConfirmationPopup() {
        this._modalRef = this.modalDialogService.openDialog(OddChangesConfirmationComponent, {
            settings: this.getModalSettings(),
        });
    }

    get modelRef(): ModalRef {
        return this._modalRef;
    }

    private getModalSettings(): DialogSettings {
        return {
            cssClass: 'odds-change-preference',
            isModalDialog: true,
            fit: true,
            hideCloseButton: true,
            scrollFreezeType: ScrollFreezeServiceTypes.All,
        };
    }

    updatePlayerPreference(playerPreference: PlayerPreference) {
        return this.apiService.post('playerpreference/updateplayerpreference', playerPreference, {
            cache: false,
            retryCount: 0,
            showSpinner: false,
            resolveWithFullResponse: true,
        });
    }

    isEligibleForOddsChangePopup() {
        if (
            !this.oddsConfig.displayOddsChangePreferencePopup ||
            !this.userService.isAuthenticated ||
            !this.oddsConfig.oddsChangePopupEligibilityDate
        ) {
            return of(false);
        }

        return this.registrationInfoService.registrationInformation.pipe(
            map((registrationInfo) => {
                if (registrationInfo?.date) {
                    const eligibilityDate = new Date(this.oddsConfig.oddsChangePopupEligibilityDate!);
                    const registrationDate = new Date(registrationInfo.date);
                    return registrationDate.getTime() > eligibilityDate.getTime();
                }

                return false;
            }),
        );
    }

    checkAndOpenOddsChangePopup(): void {
        this.playerAttributesService.playerAttributes.pipe(take(1)).subscribe((playerAttributes: PlayerAttributes | null) => {
            if (playerAttributes) {
                const isAcknowledged = playerAttributes['acknowledgement']?.['oddsChangePreference']?.value;
                if (isAcknowledged === 'false' || !isAcknowledged) {
                    this.openOddChangesConfirmationPopup();
                }
            }
        });
    }
}
